import { Injectable, EventEmitter } from '@angular/core';
import { DataService } from './data.service';
import { FilterProvider, FilterExpression, TransformationService, StorageService, SelectionService, ConditionExpression, IConditionExpression } from '@luzula/luzula-lib';

@Injectable({
	providedIn: 'root'
})
export class FilterService implements FilterProvider {
	private static readonly k = 'conditionExpressionObjects';
	filtersUpdated = new EventEmitter<void>();

	apply = new EventEmitter<void>();

	filteredDocIds: Set<string> = new Set<string>();
	currentFilterFunc?: (doc: any) => boolean;

	/**
	 * Setzt die gefilterten IDs direkt (für Projekt-Filterung client-seitig)
	 */
	setFilteredDocIds(ids: Set<string> | string[]) {
		this.filteredDocIds = ids instanceof Set ? ids : new Set(ids);
		this.apply.emit();
	}

	// Aktuelle Filter-Bounds (null = keine Bounds-Einschränkung)
	// Verwendet eigenes Interface statt Leaflet.Point, da nur x/y benötigt werden
	private filterBounds: { l: { x: number, y: number }, u: { x: number, y: number } } | null = null;

	constructor(
		private ds: DataService,
		private storage: StorageService,
		private ts: TransformationService,
		private selectionService: SelectionService
	) { 
		this.updateConditionExpressionObjects();
		this.ds.checkedOut.subscribe(() => this.updateConditionExpressionObjects());
		this.ds.checkedIn.subscribe(() => this.updateConditionExpressionObjects());
		this.ds.discarded.subscribe(() => this.updateConditionExpressionObjects());
	}

	updateConditionExpressionObjects() {
		this.ds.getFilter()?.then((doc: any) => {
			const views = doc.views;
			const conditionExpressionObjects: any = {};
			for (const [key, value] of Object.entries(views)) {
				const v = value as any;
				if (key.endsWith('_checkout')) continue;
				if (!v.object) continue;
				conditionExpressionObjects[key] = (value as any).object;
			}
			this.storage.set(FilterService.k, conditionExpressionObjects).then(() => this.filtersUpdated.emit());
		})
	}
	
	async set(key: string, value: FilterExpression) {
		let conds = await this.storage.get(FilterService.k) as any;
		if (!conds) {
			conds = {};
		}
		conds[key] = value.toObject();
		await this.storage.set(FilterService.k, conds);
		this.filtersUpdated.emit();
	}

	async list_keys() {
		const conds = await this.storage.get(FilterService.k);
		if (!conds) return [];
		return Object.keys(conds);
	}

	async get(key: string): Promise<ConditionExpression | undefined> {
		const conds = await this.storage.get(FilterService.k) as any;
		if (!conds) return undefined;
		const condExp = conds[key] as IConditionExpression;
		if (!condExp || !condExp.type) return undefined;
		return ConditionExpression.conditionExpression(condExp);
	}
	
	getAll() {
		return this.storage.get(FilterService.k) as any;
	}
	
	async remove(key: string) {
		const conds = await this.storage.get(FilterService.k) as any;
		if (!conds) return;
		delete conds[key];
		await this.storage.set(FilterService.k, conds);
	}

	/**
	 * Setzt die Bounds für den nächsten Filter-Aufruf.
	 * Bei null wird ohne Bounds-Einschränkung gefiltert.
	 */
	setFilterBounds(bounds: { l: { x: number, y: number }, u: { x: number, y: number } } | null): void {
		this.filterBounds = bounds;
	}

	/**
	 * Wendet den Filter an - holt die gefilterten IDs aus der View.
	 * storeViews muss vorher aufgerufen worden sein!
	 */
	async applyFilter(name: string) {
		// View abrufen und gefilterte IDs speichern
		try {
			const docs = await this.ds.getView(name);
			this.filteredDocIds.clear();
			if (docs?.rows) {
				docs.rows.forEach((d: any) => {
					this.filteredDocIds.add(d.key);
				});
			}
			this.apply.emit();
		} catch (reason) {
			console.error(reason);
		}
	}

	/**
	 * Setzt die Filterfunktion ohne DB-Operationen (für client-seitige Filterung)
	 */
	setFilterFunc(exp: FilterExpression): void {
		const func = exp.filterFunction(false, false, []);
		this.currentFilterFunc = (doc: any): boolean => {
			let f: (doc: any) => boolean;
			eval(`f = ${func}`);
			return f!(doc);
		};
	}

	/**
	 * Wendet den Filter client-seitig auf die übergebenen Dokumente an.
	 * Schneller als DB-basierte Filterung.
	 */
	applyFilterClientSide(docs: any[]): void {
		this.filteredDocIds.clear();
		if (!this.currentFilterFunc) return;

		docs.forEach(doc => {
			if (this.currentFilterFunc!(doc)) {
				this.filteredDocIds.add(doc._id);
			}
		});
		this.apply.emit();
	}

	async storeViews(name: string, exp: FilterExpression, withBounds: boolean, bnds: {l: {x: number, y: number}, u: {x: number, y: number}}[], callback: Function) {
		const func = exp.filterFunction(false, withBounds, bnds);
		this.currentFilterFunc = (doc: any): boolean => {
			let f: (doc: any) => boolean;
			eval(`f = ${func}`);
			return f!(doc);
		};
		return this.ds.storeViews(callback, withBounds, bnds);
	}
	
	async resetFilter() {
		await this.storage.remove('currentFilter');
		delete this.currentFilterFunc;
		this.filteredDocIds.clear();
		this.filtersUpdated.emit();
	}
}
